import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiInstallation from 'api/installationPrices';
import BasicTable from 'components/layout/tables/BasicTable';
import SearchInput from 'components/layout/forms/SearchInput';
import IInstallationPrice from 'interfaces/products/IInstallationPrice';
import {
  INSTALLATION_CATEGORY,
  OTHER_EXPENSE_CATEGORY,
  useChoiceList,
} from 'services/offers/offerChoices';
import {
  columnSearchHeader,
  useColumnSearch,
} from 'services/tables/columnSearch';
import { withContentWidths } from 'services/tables/getColumnWidths';

import getInstallationPriceColumns from './installationPriceColumns';
import translations from '../../../../services/translations/translations';

const InstallationPricesList = (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([translations.products, translations.common]);

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<number>();
  const [sizePerPage, setSizePerPage] = useState(50);
  const [totalSize, setTotalSize] = useState<number>();
  const [installationPrices, setInstallationPrices] = useState<
    IInstallationPrice[]
  >([]);

  const [searchTerms, setSearchTerms] = useState<string>('');
  // The searches of the columns, from the first page
  const columnSearch = useColumnSearch(() => {
    setPage(1);
    setOffset(0);
  });
  // The categories of the installations and the other expenses for the search of the category
  const categories = useChoiceList(INSTALLATION_CATEGORY).concat(
    useChoiceList(OTHER_EXPENSE_CATEGORY)
  );
  const selectedInstallationPrice =
    installationPrices !== undefined && selectedRow !== undefined
      ? installationPrices[selectedRow]
      : undefined;

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(
        `/products/installation-prices/${selectedInstallationPrice?.id}`
      );
    }
  }, [history, selectedInstallationPrice, selectedRow]);

  useEffect(() => {
    const getInstallationPrices = async () => {
      try {
        const list = (await apiInstallation.get({
          limit: sizePerPage,
          offset,
          search: searchTerms,
          filters: columnSearch.applied,
        })) as IInstallationPrice[];

        const count = await apiInstallation.count(
          searchTerms,
          columnSearch.applied
        );
        setInstallationPrices(list);
        setTotalSize(count.count);
      } catch (error) {
        // define action
      }
    };
    getInstallationPrices().catch((e) => console.log(e));
  }, [columnSearch.applied, offset, searchTerms, sizePerPage]);

  // The searches of the columns in their order: name, category (a choice), price
  const searchKeys = ['name', 'category', 'price'];
  const columns = getInstallationPriceColumns(t, translations).map(
    (column, index) => ({
      ...column,
      headerFormatter: columnSearchHeader({
        onChange: (value) => columnSearch.setValue(searchKeys[index], value),
        options:
          searchKeys[index] === 'category'
            ? [
                { label: t(`${translations.products}:All choices`), value: '' },
              ].concat(categories)
            : undefined,
        placeholder: t(`${translations.products}:Search column`),
        value: columnSearch.search[searchKeys[index]] ?? '',
      }),
    })
  );

  const sized = withContentWidths(columns, installationPrices || []);

  const handleTableChange = (type: any, { page, sizePerPage }: any) => {
    setPage(page);
    setSizePerPage(sizePerPage);
    setOffset((page - 1) * sizePerPage);
  };

  return (
    <>
      <Row>
        <Col xs={3}>
          <SearchInput setSearchTerms={setSearchTerms} />
        </Col>
      </Row>
      {/* The widths of the columns follow their content */}
      <div
        className="table-scroll"
        style={{ '--table-min-width': sized.minWidth } as React.CSSProperties}
      >
        <BasicTable
          columns={sized.columns}
          data={installationPrices}
          page={page}
          sizePerPage={sizePerPage}
          totalSize={totalSize}
          handleTableChange={handleTableChange}
          setSelectedRow={setSelectedRow}
          keyField="id"
        />
      </div>
    </>
  );
};

export default InstallationPricesList;
